import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Button,
  Chip,
  IconButton,
  Avatar,
  Stack,
  Pagination,
  Dialog,
  DialogActions,
  InputAdornment,
  Grid
} from "@mui/material";
import { 
  Search, 
  Filter, 
  Eye, 
  CheckCircle, 
  ExternalLink, 
  Plus, 
  ChevronLeft,
  Calendar,
  Wallet
} from "lucide-react";
import { toast } from "react-toastify";
import DetalleGasto from "./detalleGasto";

const PALETTE = {
  ice: "#C2EDF0",
  sky: "#99D9DF",
  aqua: "#00C0CA",
  teal: "#008B9C",
  azur: "#28708B",
  dark: "#22404D",
  pearl: "#EEF3F4",
  red: "#F65F4E",
  lime: "#8FC440"
};

const ListaGastos = () => {
  const { idPrograma } = useParams();
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();

  const [gastos, setGastos] = useState([]);
  const [gastosFiltrados, setGastosFiltrados] = useState([]);
  const [gastoSeleccionado, setGastoSeleccionado] = useState(null);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [paginaActual, setPaginaActual] = useState(1);
  const itemsPorPagina = 8;

  const [filtros, setFiltros] = useState({
    general: "",
    rubro: "",
    proveedor: "",
    ordenarPorFecha: "desc",
  });

  useEffect(() => {
    const obtenerGastos = async () => {
      try {
        let response = await fetch(`${apiUrl}/Gasto/GastoPorPrograma?programaId=${idPrograma}`, {
          headers: { "Content-Type": "application/json", apiKey: localStorage.getItem("apiKey") }
        });
        if (response.ok) {
          let data = await response.json();
          setGastos(data);
          setGastosFiltrados(data);
        } else {
          toast.error("No se pudieron cargar los gastos");
        }
      } catch (error) { console.error(error); }
    };
    obtenerGastos();
  }, [apiUrl, idPrograma]);

  useEffect(() => {
    let filtrados = gastos.filter(g => 
      g.proveedor.toLowerCase().includes(filtros.general.toLowerCase()) ||
      g.rubro.toLowerCase().includes(filtros.general.toLowerCase())
    );

    if (filtros.rubro) {
        filtrados = filtrados.filter(g => g.rubro === filtros.rubro);
    }

    filtrados.sort((a, b) => {
      const dateA = new Date(a.fecha);
      const dateB = new Date(b.fecha);
      return filtros.ordenarPorFecha === "asc" ? dateA - dateB : dateB - dateA;
    });

    setGastosFiltrados(filtrados);
    setPaginaActual(1);
  }, [filtros, gastos]);

  const aprobarGasto = async (id) => {
    try {
      const response = await fetch(`${apiUrl}/Gasto/Aprobar?id=${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", apiKey: localStorage.getItem("apiKey") }
      });
      if (response.ok) {
        toast.success("Gasto aprobado con éxito");
        setGastos(prev => prev.map(g => g.id === id ? { ...g, estado: "Aprobado" } : g));
      }
    } catch (error) { toast.error("Error al aprobar"); }
  };

  // Paginación
  const totalPages = Math.ceil(gastosFiltrados.length / itemsPorPagina);
  const gastosPaginados = gastosFiltrados.slice((paginaActual - 1) * itemsPorPagina, paginaActual * itemsPorPagina);

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, mt: "64px", bgcolor: "#F8FAFB", minHeight: "100vh" }}>
      
      {/* HEADER */}
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" sx={{ mb: 4 }} spacing={2}>
        <Box>
            <Button 
                startIcon={<ChevronLeft size={18}/>} 
                onClick={() => navigate(-1)}
                sx={{ color: PALETTE.azur, textTransform: 'none', fontWeight: 700, mb: 1 }}
            >
                Volver al panel
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 900, color: PALETTE.dark }}>
                Historial de Gastos
            </Typography>
           
        </Box>

        {localStorage.getItem("rol") === "coordinador" && (
          <Button 
            variant="contained" 
            startIcon={<Plus />}
            onClick={() => navigate("/FormCajaChica")}
            sx={{ 
                bgcolor: PALETTE.teal, borderRadius: '12px', px: 3, py: 1.2, fontWeight: 800,
                '&:hover': { bgcolor: PALETTE.dark }
            }}
          >
            Nuevo Gasto
          </Button>
        )}
      </Stack>

      {/* FILTROS */}
      <Card sx={{ p: 2, mb: 3, borderRadius: '20px', border: '1px solid #eee', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por proveedor o concepto..."
              value={filtros.general}
              onChange={(e) => setFiltros({...filtros, general: e.target.value})}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} color={PALETTE.azur} />
                  </InputAdornment>
                ),
                sx: { borderRadius: '12px', bgcolor: '#fcfcfc' }
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
                select
                fullWidth
                size="small"
                label="Orden"
                value={filtros.ordenarPorFecha}
                onChange={(e) => setFiltros({...filtros, ordenarPorFecha: e.target.value})}
                InputProps={{ sx: { borderRadius: '12px' } }}
            >
                <MenuItem value="desc">Más recientes primero</MenuItem>
                <MenuItem value="asc">Más antiguos primero</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={5} sx={{ textAlign: 'right' }}>
             <Typography variant="caption" sx={{ fontWeight: 700, color: PALETTE.azur }}>
                Mostrando {gastosFiltrados.length} resultados
             </Typography>
          </Grid>
        </Grid>
      </Card>

      {/* TABLA ESTILIZADA */}
      <TableContainer component={Paper} sx={{ borderRadius: '24px', boxShadow: '0 10px 30px rgba(0,0,0,0.03)', border: '1px solid #eee' }}>
        <Table>
          <TableHead sx={{ bgcolor: PALETTE.pearl }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 800, color: PALETTE.dark }}>Proveedor / Detalle</TableCell>
              <TableCell sx={{ fontWeight: 800, color: PALETTE.dark }}>Rubro</TableCell>
              <TableCell sx={{ fontWeight: 800, color: PALETTE.dark }}>Importe</TableCell>
              <TableCell sx={{ fontWeight: 800, color: PALETTE.dark }}>Fecha</TableCell>
              <TableCell sx={{ fontWeight: 800, color: PALETTE.dark }}>Estado</TableCell>
              <TableCell align="center" sx={{ fontWeight: 800, color: PALETTE.dark }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {gastosPaginados.map((g) => (
              <TableRow key={g.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: PALETTE.ice, color: PALETTE.azur, width: 36, height: 36 }}>
                      <Wallet size={18} />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{g.proveedor}</Typography>
                      <Typography variant="caption" color="text.secondary">{g.nombrePrograma}</Typography>
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Chip label={g.rubro} size="small" sx={{ fontWeight: 600, bgcolor: PALETTE.pearl }} />
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: PALETTE.teal }}>
                    {g.moneda} {g.importe.toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Calendar size={14} color={PALETTE.azur}/>
                        <Typography variant="body2">{new Date(g.fecha).toLocaleDateString("es-ES")}</Typography>
                    </Stack>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={g.estado} 
                    size="small"
                    sx={{ 
                      fontWeight: 800, 
                      bgcolor: g.estado === "Aprobado" ? `${PALETTE.lime}20` : `${PALETTE.red}10`,
                      color: g.estado === "Aprobado" ? PALETTE.lime : PALETTE.red,
                      border: `1px solid ${g.estado === "Aprobado" ? PALETTE.lime : PALETTE.red}30`
                    }} 
                  />
                </TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={1} justifyContent="center">
                    <Tooltip title="Ver detalle">
                        <IconButton size="small" onClick={() => { setGastoSeleccionado(g.id); setMostrarModal(true); }}>
                            <Eye size={18} color={PALETTE.azur} />
                        </IconButton>
                    </Tooltip>
                    
                    {g.linkComprobante && (
                        <IconButton size="small" component="a" href={g.linkComprobante} target="_blank">
                            <ExternalLink size={18} color={PALETTE.teal} />
                        </IconButton>
                    )}

                    {g.estado !== "Aprobado" && localStorage.getItem("rol")?.toLowerCase() === "finanzas" && (
                        <IconButton size="small" onClick={() => aprobarGasto(g.id)}>
                            <CheckCircle size={18} color={PALETTE.lime} />
                        </IconButton>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        
        {gastosFiltrados.length === 0 && (
          <Box sx={{ p: 5, textAlign: 'center' }}>
            <Typography color="text.secondary">No se encontraron gastos con los filtros aplicados</Typography>
          </Box>
        )}
      </TableContainer>

      {/* PAGINACIÓN MUI */}
      <Stack alignItems="center" sx={{ mt: 4 }}>
        <Pagination 
            count={totalPages} 
            page={paginaActual} 
            onChange={(e, value) => setPaginaActual(value)}
            sx={{ 
                '& .Mui-selected': { bgcolor: `${PALETTE.teal} !important`, color: 'white' },
                '& .MuiPaginationItem-root': { fontWeight: 700 }
            }} 
        />
      </Stack>

      {/* MODAL DETALLE */}
      <Dialog 
        open={mostrarModal} 
        onClose={() => setMostrarModal(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{ sx: { borderRadius: '28px' } }}
      >
        <Box sx={{ p: 2 }}>
            <DetalleGasto idGasto={gastoSeleccionado} />
        </Box>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setMostrarModal(false)} sx={{ fontWeight: 800, color: PALETTE.dark }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

// Helper para Tooltip (si no lo importaste)
const Tooltip = ({ title, children }) => <Box title={title}>{children}</Box>;

export default ListaGastos;